// Drives the one full-screen lotus loader, #sc-boot-loader in client/index.html.
// It lives outside #app so React never replaces it: the same <img> breathes
// from first paint until the app is ready, instead of being swapped for a fresh
// copy that restarts the breath (or paints blank for a frame) mid-cycle.
//
// These timings mirror the .sc-boot-loader CSS in client/index.html, which must
// run before any JS loads. AppLoader.spec.tsx asserts the two agree.

// The lotus fades in only after this long, so a quick load never shows it.
export const LOTUS_DELAY_MS = 300;
// Once the lotus has started to show, keep it at least this long so it never
// flashes on and straight off.
export const LOTUS_MIN_VISIBLE_MS = 600;
// How long the whole loader fades out over the app once nothing holds it.
export const FADE_OUT_MS = 250;

export const BOOT_LOADER_ID = "sc-boot-loader";
const LEAVING_CLASS = "sc-boot-loader--leaving";

// index.html paints the loader before any JS runs, so it starts out held by the
// boot itself (released by releaseBootLoader) and shown at navigation start.
let holds = 1;
let bootReleased = false;
let shownAt = 0;
let leaveTimer: number | undefined;
let hideTimer: number | undefined;

const getLoader = () => document.getElementById(BOOT_LOADER_ID);

const clearTimers = () => {
  window.clearTimeout(leaveTimer);
  window.clearTimeout(hideTimer);
};

const show = () => {
  clearTimers();
  const loader = getLoader();
  if (!loader) return;
  if (loader.hidden) {
    // Coming back from display:none restarts the CSS fade-in, so the delay
    // counts from now.
    loader.hidden = false;
    shownAt = performance.now();
  }
  loader.classList.remove(LEAVING_CLASS);
};

const leave = () => {
  const loader = getLoader();
  if (!loader) return;
  loader.classList.add(LEAVING_CLASS);
  hideTimer = window.setTimeout(() => {
    loader.hidden = true;
  }, FADE_OUT_MS);
};

const release = () => {
  holds -= 1;
  if (holds > 0) return;
  clearTimers();
  const elapsed = performance.now() - shownAt;
  // Before the delay the lotus has not shown yet, so the loader can go now.
  // After it, wait out the minimum so the lotus does not flash.
  const wait =
    elapsed < LOTUS_DELAY_MS
      ? 0
      : Math.max(0, LOTUS_DELAY_MS + LOTUS_MIN_VISIBLE_MS - elapsed);
  // Even a zero wait goes through a timer, so a hold taken later in the same
  // task (the next commit's AppLoader) cancels the fade before it starts.
  leaveTimer = window.setTimeout(leave, wait);
};

// Keeps the loader on screen until the returned function is called.
export const holdBootLoader = (): (() => void) => {
  holds += 1;
  show();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    release();
  };
};

// Drops the boot's own hold, once React's first commit has run (or the boot
// gave up). Safe to call more than once.
export const releaseBootLoader = () => {
  if (bootReleased) return;
  bootReleased = true;
  release();
};
